import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Search, Building2, Crown, X, AlertCircle, Check, ShieldAlert, Trash2 } from 'lucide-react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../config/api';
import { useSubscriptionStore, SubscriptionPlan } from '../../stores/subscriptionStore';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';
import { TIMEZONE_OPTIONS } from '../../utils/timezone';
import { formatPrice, formatLimit, getEffectivePrice } from '../../utils/subscriptionFormat';
import { DeactivateConfirmModal } from '../deletion/DeactivateConfirmModal';
import type { CascadeImpact } from '../deletion/index';
import { useEhrRegistry } from '../../hooks/useEhrRegistry';
import { useAuth } from '../../hooks/useAuth';
import { canUseConsoleAction, type ConsoleAction } from '../../utils/consoleActionAccess';
import { ORGANIZATION_LANGUAGE_OPTIONS, withStoredOption } from '../../utils/organizationOptions';
import { TIER_BADGE_BORDER_CLASSES, tierClasses } from '../../utils/planTiers';

// Partner type for the search results
interface PartnerOption {
  id: string;
  name: string;
  slug: string;
  type: 'reseller' | 'affiliate' | 'enterprise' | 'internal';
  status: string;
  isOwnerPartner: boolean;
  organizationCount: number;
  quotaUsage: {
    organizations: number;
    maxOrganizations: number;
    isUnlimited: boolean;
  };
}

// Organization type - compatible with backend model
export interface Organization {
  id?: string;
  name: string;
  subdomain?: string;
  type?: 'hospital' | 'clinic' | 'practice' | 'other';
  partnerId?: string;
  /** Create only, and optional: with no plan the organization is created without a subscription. */
  planId?: string;
  partner?: {
    id: string;
    name: string;
    slug: string;
    type: string;
    status: string;
  } | null;
  // Flattened partner fields (from API response)
  partnerName?: string;
  partnerType?: string;
  partnerIsOwner?: boolean;
  adminEmail?: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  contact: {
    email: string;
    phone: string;
    adminName: string;
  };
  // Subscription data is managed separately via the Subscription model
  // Use SubscriptionPlanSelector component to view/assign plans
  subscription?: {
    planId?: string;
    plan?: string;
    /** Display name of the plan, sent by the organizations list endpoint. */
    planName?: string;
    status?: string;
    included?: {
      users?: number;
      encountersPerMonth?: number;
      transcriptionMinutes?: number;
      storageGB?: number;
    };
  };
  compliance?: {
    baaSignedDate?: string;
    baaDocumentId?: string;
    hipaaTrainingCompleted: boolean;
    lastAuditDate?: string;
  };
  settings: {
    operationMode: 'standalone' | 'connected';
    externalTenantId?: string;
    externalSystemId?: string;
    webhookId?: string | null;
    /** Read-only. The list endpoint that resolves an id is superadmin-only. */
    webhookName?: string | null;
    /** Read-only. Null when `webhookId` is unset or no longer resolves. */
    webhookActive?: boolean | null;
    externalSystemTimezone?: string;
    timezone?: string;
    preferredLanguage?: string;
    externalSystems?: Array<{
      systemId: string;
      displayName: string;
      status: 'active' | 'inactive' | 'error';
      isPrimary: boolean;
    }>;
    // Clinical coding feature flags (per-organization)
    emCodingAssistantEnabled?: boolean;
    cptCodingAssistantEnabled?: boolean;
  };
  isActive?: boolean;
  isDeleted?: boolean;
  deletedAt?: string;
  userCount?: number;
  createdAt?: string;
}

interface OrganizationModalProps {
  mode: 'create' | 'edit';
  organization?: Organization | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (org: Organization) => Promise<void>;
}

const emptyOrg: Organization = {
  name: '',
  subdomain: '',
  type: 'clinic',
  adminEmail: '',
  address: {
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA'
  },
  contact: {
    email: '',
    phone: '',
    adminName: ''
  },
  // Subscription is managed separately via SubscriptionPlanSelector
  compliance: {
    hipaaTrainingCompleted: false,
    baaSignedDate: '',
    lastAuditDate: ''
  },
  settings: {
    operationMode: 'standalone',
    timezone: 'America/New_York',
    preferredLanguage: 'en-US',
    externalTenantId: '',
    externalSystems: [],
    emCodingAssistantEnabled: true,
    cptCodingAssistantEnabled: true,
  },
  isActive: true
};

/**
 * The API returns compliance dates as full ISO timestamps, which a date input rejects and renders
 * blank. Left as-is, opening the modal and saving would submit an empty value and clear the stored
 * date.
 */
const toDateInputValue = (value?: string): string => (value ? String(value).slice(0, 10) : '');

export const OrganizationModal: React.FC<OrganizationModalProps> = ({
  mode,
  organization,
  open,
  onOpenChange,
  onSave
}) => {
  const [formData, setFormData] = useState<Organization>(emptyOrg);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('organization');

  // Data-driven EHR system registry (replaces hard-coded EHR options)
  const { options: ehrOptions, systems: ehrSystems } = useEhrRegistry();

  // Console roles other than superadmin share this modal, so anything superadmin-only has to be
  // gated here rather than fired and left to fail: the axios interceptor turns every 403 into an
  // "Access denied" toast before the caller's own error handling runs.
  const { user, hasPermission } = useAuth();
  // The inbound EHR identity is the enterprise admin's own to set; it already sets all of it when
  // creating the organization. A webhook row has no owner, so pointing an organization at one is
  // not confined to the caller's tenancy.
  const canManageEhrIdentity = user?.role === 'superadmin' || user?.role === 'enterprise_admin';
  // Both of the webhook endpoint's checks, not just the role, and read off the granted list rather
  // than through hasPermission: the permission is excluded from `platform.*`, and hasPermission
  // answers true for every `platform.` string a superadmin asks about, so it cannot see that.
  const canManageDeliveryWebhook = user?.role === 'superadmin'
    && user?.permissions?.includes('platform.external_integrations.manage') === true;
  // Which clinical codes an organization sees is a product decision, and the server refuses a
  // change to either flag from anyone but platform staff. Separate flag from external routing so
  // neither gate moves by accident when the other does.
  const canManageCodingFeatures = user?.role === 'superadmin';
  // The search forces the caller's enterprise, so it offers only partners the server would accept.
  const canSelectPartner = hasPermission('platform.partners.view')
    && (mode === 'create' || user?.role === 'superadmin' || user?.role === 'enterprise_admin');
  // Lifecycle controls are duplicated from the Organizations list, so they consult the same table.
  const allowed = (action: ConsoleAction) => canUseConsoleAction(action, { role: user?.role, hasPermission });
  // Two different authorities, deliberately not one. The compliance attestations ride on the wide
  // platform grant and stay with platform staff; reading one organization's own subscription is what
  // a scoped console role needs to use its assign grant. Collapsing them would hand a tenant role
  // the HIPAA/BAA controls.
  const canViewSubscriptions = hasPermission('platform.subscriptions.view')
    || allowed('viewOrganizationSubscription');
  const canEditCompliance = allowed('signComplianceAttestations');

  // Active webhooks for the delivery-target dropdown. Superadmin-only endpoint: the middleware's
  // first check is the role, so no grant can make this succeed for anyone else.
  const [webhookOptions, setWebhookOptions] = useState<Array<{ id: string; name: string; isActive: boolean }>>([]);
  useEffect(() => {
    if (!canManageDeliveryWebhook) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.get<{ success: boolean; data: Array<{ id: string; name: string; isActive: boolean }> }>(
          '/v1/platform/external-integrations/webhooks'
        );
        if (!cancelled) setWebhookOptions(res?.data ?? []);
      } catch {
        // Non-fatal: dropdown simply shows no options.
      }
    })();
    return () => { cancelled = true; };
  }, [canManageDeliveryWebhook]);

  // The EHR default webhook for this org's EHR (used as the dropdown's inherit hint).
  const orgEhrDefault = ehrSystems.find((s) => s.slug === formData.settings?.externalSystemId);
  const orgEhrDefaultWebhookId = orgEhrDefault?.defaultWebhookId ?? null;
  const orgEhrDefaultWebhookName = webhookOptions.find((w) => w.id === orgEhrDefaultWebhookId)?.name
    ?? orgEhrDefault?.defaultWebhookName
    ?? undefined;

  // Where deliveries actually go, for the roles that see this as a value rather than a control.
  const selectedWebhookId = formData.settings?.webhookId;
  const selectedWebhookName = formData.settings?.webhookName
    ?? webhookOptions.find((w) => w.id === selectedWebhookId)?.name;
  const deliveryTargetLabel = selectedWebhookId
    ? selectedWebhookName ?? 'Selected webhook no longer exists — nothing is delivered'
    : orgEhrDefaultWebhookName ? `EHR default (${orgEhrDefaultWebhookName})` : 'None';

  // An inactive target pauses delivery, and `webhookOptions` is empty for every role that cannot
  // read the webhook list, so the payload's own flag is what makes this reachable for them.
  const deliveryTargetInactive = selectedWebhookId
    ? formData.settings?.webhookActive === false
      || webhookOptions.some((w) => w.id === selectedWebhookId && !w.isActive)
    : orgEhrDefaultWebhookId !== null && orgEhrDefault?.defaultWebhookActive === false;

  // Lifecycle management state
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [cascadeImpact, setCascadeImpact] = useState<CascadeImpact | undefined>();
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [showHardDeleteModal, setShowHardDeleteModal] = useState(false);
  const [hardDeleteConfirmName, setHardDeleteConfirmName] = useState('');
  const [hardDeleteCheckbox, setHardDeleteCheckbox] = useState(false);
  const [isHardDeleting, setIsHardDeleting] = useState(false);

  // Partner search state
  const [partnerSearch, setPartnerSearch] = useState('');
  const [partnerOptions, setPartnerOptions] = useState<PartnerOption[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<PartnerOption | null>(null);
  const [isSearchingPartners, setIsSearchingPartners] = useState(false);
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);

  // Search partners with debounce
  const searchPartners = useCallback(async (query: string) => {
    if (!query || query.length < 1) {
      // Load all partners if no query
      setIsSearchingPartners(true);
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/v1/platform/partners/search?limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success) {
          setPartnerOptions(data.data || []);
        }
      } catch (err) {
        console.error('Failed to load partners:', err);
      } finally {
        setIsSearchingPartners(false);
      }
      return;
    }

    setIsSearchingPartners(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/v1/platform/partners/search?q=${encodeURIComponent(query)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPartnerOptions(data.data || []);
      }
    } catch (err) {
      console.error('Failed to search partners:', err);
    } finally {
      setIsSearchingPartners(false);
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (showPartnerDropdown) {
        searchPartners(partnerSearch);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [partnerSearch, showPartnerDropdown, searchPartners]);

  // Deep merge utility to ensure all fields exist even if missing from DB
  const deepMerge = (defaults: any, data: any): any => {
    if (!data) return defaults;

    const result = { ...defaults };

    for (const key in data) {
      if (data[key] !== null && typeof data[key] === 'object' && !Array.isArray(data[key])) {
        result[key] = deepMerge(defaults[key] || {}, data[key]);
      } else {
        result[key] = data[key];
      }
    }

    return result;
  };

  useEffect(() => {
    if (open) {
      const mergedData = organization ? deepMerge(emptyOrg, organization) : emptyOrg;
      // Populate externalSystemTimezone from external systems array if available
      if (mergedData.settings?.externalSystems?.[0]?.timezone && !mergedData.settings?.externalSystemTimezone) {
        mergedData.settings.externalSystemTimezone = mergedData.settings.externalSystems[0].timezone;
      }
      // Same for the identity: a value the form displays but does not hold is never submitted.
      if (mergedData.settings?.externalSystems?.[0]?.systemId && !mergedData.settings?.externalSystemId) {
        mergedData.settings.externalSystemId = mergedData.settings.externalSystems[0].systemId;
      }
      setFormData(mergedData);
      setError(null);
      setActiveTab('organization');
      setPartnerSearch('');
      setShowPartnerDropdown(false);

      // Set selected partner from organization data
      if (organization?.partner) {
        setSelectedPartner({
          id: organization.partner.id,
          name: organization.partner.name,
          slug: organization.partner.slug,
          type: organization.partner.type as PartnerOption['type'],
          status: organization.partner.status,
          isOwnerPartner: false,
          organizationCount: 0,
          quotaUsage: { organizations: 0, maxOrganizations: 0, isUnlimited: false }
        });
      } else {
        setSelectedPartner(null);
      }
    }
  }, [open, organization]);

  // Auto-generate subdomain from name
  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      subdomain: mode === 'create' ? name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 63) : prev.subdomain
    }));
  };

  const handleSelectPartner = (partner: PartnerOption) => {
    setSelectedPartner(partner);
    setFormData(prev => ({ ...prev, partnerId: partner.id }));
    setShowPartnerDropdown(false);
    setPartnerSearch('');
  };

  const handleClearPartner = () => {
    setSelectedPartner(null);
    setFormData(prev => ({ ...prev, partnerId: undefined }));
  };

  // --- Lifecycle Handlers ---
  const initiateDeactivate = async () => {
    if (!formData.id) return;
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/v1/platform/organizations/${formData.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const result = await response.json();
      // Without this the refusal is silent: a 403 body simply has no requiresConfirmation, so the
      // button appears to do nothing at all.
      if (!response.ok) {
        toast.error(result.error || `Failed to get deactivation info (${response.status})`);
        return;
      }
      if (result.requiresConfirmation) {
        setCascadeImpact(result.cascadeImpact);
        setShowDeactivateModal(true);
      }
    } catch (err) {
      toast.error('Failed to get deactivation info');
    }
  };

  const handleDeactivateConfirm = async (confirmName: string) => {
    if (!formData.id) return;
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    setIsDeactivating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/platform/organizations/${formData.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmName })
      });
      const result = await response.json();
      if (result.success) {
        setShowDeactivateModal(false);
        setFormData(prev => ({ ...prev, isDeleted: true, deletedAt: new Date().toISOString() }));
        toast.success('Organization deactivated successfully');
        onSave(formData); // Trigger list refresh
      } else {
        toast.error(result.error || 'Failed to deactivate');
      }
    } catch (err) {
      toast.error('Failed to deactivate organization');
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleReactivate = async () => {
    if (!formData.id) return;
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    setIsReactivating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/platform/organizations/${formData.id}/reactivate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (result.success) {
        setShowReactivateConfirm(false);
        setFormData(prev => ({ ...prev, isDeleted: false, deletedAt: undefined }));
        toast.success(`Organization reactivated. ${result.data?.usersReactivated || 0} user(s) restored.`);
        onSave(formData);
      } else {
        toast.error(result.error || 'Failed to reactivate');
      }
    } catch (err) {
      toast.error('Failed to reactivate organization');
    } finally {
      setIsReactivating(false);
    }
  };

  const handleHardDelete = async () => {
    if (!formData.id) return;
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    setIsHardDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/platform/organizations/${formData.id}/permanent`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmName: hardDeleteConfirmName, confirmCheckbox: hardDeleteCheckbox })
      });
      const result = await response.json();
      if (result.success) {
        setShowHardDeleteModal(false);
        onOpenChange(false);
        toast.success(`Organization "${result.data.organizationName}" permanently deleted`);
        onSave(formData); // Trigger list refresh
      } else {
        toast.error(result.error || 'Failed to delete permanently');
      }
    } catch (err) {
      toast.error('Failed to permanently delete organization');
    } finally {
      setIsHardDeleting(false);
    }
  };

  const canChoosePlan = allowed('viewPlanCatalog');
  // On the create path this tab holds the only plan control, so reading the catalog admits you.
  const canSeeSubscriptionTab = canViewSubscriptions || (mode === 'create' && canChoosePlan);

  // The marked fields on the Organization tab, checked here rather than left to `required`: the
  // submit button sits outside the Tabs and an inactive pane is unmounted, so the browser has
  // nothing to validate whenever Create is pressed from another tab.
  const REQUIRED_FIELDS: Array<{ label: string; value: () => string | undefined }> = [
    { label: 'Organization Name', value: () => formData.name },
    { label: 'Subdomain', value: () => formData.subdomain },
    { label: 'Street', value: () => formData.address?.street },
    { label: 'City', value: () => formData.address?.city },
    { label: 'State', value: () => formData.address?.state },
    { label: 'ZIP Code', value: () => formData.address?.zipCode },
    { label: 'Admin Name', value: () => formData.contact?.adminName },
    { label: 'Contact Email', value: () => formData.contact?.email },
    { label: 'Contact Phone', value: () => formData.contact?.phone },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const missing = REQUIRED_FIELDS.filter(field => !(field.value() ?? '').trim());
      if (missing.length > 0) {
        setError(`Please complete: ${missing.map(field => field.label).join(', ')}`);
        setActiveTab('organization');
        setIsLoading(false);
        return;
      }
      // Flatten settings fields to root level (backend expects operationMode, externalTenantId, etc. at root)
      const resolvedPartnerId = selectedPartner?.id || formData.partnerId;
      if (!resolvedPartnerId) {
        setError('Please select a partner for this organization');
        setIsLoading(false);
        return;
      }
      const dataToSave = {
        ...formData,
        partnerId: resolvedPartnerId,
        operationMode: formData.settings?.operationMode || 'standalone',
        externalTenantId: formData.settings?.externalTenantId,
        externalSystemId: formData.settings?.externalSystemId,
        webhookId: formData.settings?.webhookId ?? null,
        externalSystemTimezone: formData.settings?.externalSystemTimezone,
      };
      await onSave(dataToSave);
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save organization');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 grid grid-rows-[auto_auto_1fr_auto]">
        {/* Header */}
        <div className="border-b px-6 py-4">
          <DialogHeader>
            <DialogTitle>
              {mode === 'create' ? 'Create New Organization' : 'Edit Organization'}
            </DialogTitle>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} className="contents">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="contents">
            {/* Tab Navigation */}
            <div className="border-b px-6">
              {/* Hidden rather than disabled: an empty tab reads as missing data, and mounting it
                  fires the catalog request. */}
              <TabsList className={`grid w-full ${canSeeSubscriptionTab ? 'grid-cols-3' : 'grid-cols-2'}`}>
                <TabsTrigger value="organization">Organization</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
                {canSeeSubscriptionTab && (
                  <TabsTrigger value="subscription">Subscription</TabsTrigger>
                )}
              </TabsList>
            </div>

            {/* Scrollable Content Area */}
            <div className="overflow-y-auto px-6 py-6">
              {/* Organization Tab - combines Basic Info + Address + Contact */}
              <TabsContent value="organization" className="space-y-6 mt-0">
              {/* Basic Information Section */}
              <div className="space-y-5">
                <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                  Basic Information
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Name *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="Acme Medical Clinic"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subdomain *
                  </label>
                  <Input
                    value={formData.subdomain}
                    onChange={(e) => setFormData({...formData, subdomain: e.target.value})}
                    placeholder="acme-medical-clinic"
                    disabled={mode === 'edit'}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1.5">
                    Lowercase letters, numbers, and hyphens only
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type *
                  </label>
                  <select
                    className="w-full px-3 py-2 border rounded-md"
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value as any})}
                    required
                  >
                    <option value="hospital">Hospital</option>
                    <option value="clinic">Clinic</option>
                    <option value="practice">Practice</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Partner Selector */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Partner {mode === 'create' && '*'}
                  </label>

                  {!canSelectPartner ? (
                    // The partner search needs platform.partners.view. Without it the endpoint
                    // returns nothing, and it uses raw fetch so the failure is silent — an empty
                    // box that can never fill. Show the partner instead of a dead control.
                    <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-gray-50">
                      <Building2 className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">
                        {selectedPartner?.name || formData.partnerName || 'Your partner account'}
                      </span>
                    </div>
                  ) : selectedPartner ? (
                    // Selected partner display
                    <div className="flex items-center justify-between px-3 py-2 border rounded-md bg-gray-50">
                      <div className="flex items-center gap-2">
                        {selectedPartner.isOwnerPartner ? (
                          <Crown className="h-4 w-4 text-amber-500" />
                        ) : (
                          <Building2 className="h-4 w-4 text-gray-500" />
                        )}
                        <span className="font-medium">{selectedPartner.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          selectedPartner.type === 'internal' ? 'bg-purple-100 text-purple-700' :
                          selectedPartner.type === 'enterprise' ? 'bg-blue-100 text-blue-700' :
                          selectedPartner.type === 'reseller' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {selectedPartner.type}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleClearPartner}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <X className="h-4 w-4 text-gray-500" />
                      </button>
                    </div>
                  ) : (
                    // Search input
                    <div className="relative">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          type="text"
                          placeholder="Search partners..."
                          value={partnerSearch}
                          onChange={(e) => setPartnerSearch(e.target.value)}
                          onFocus={() => {
                            setShowPartnerDropdown(true);
                            searchPartners('');
                          }}
                          className="pl-9"
                        />
                      </div>

                      {/* Dropdown */}
                      {showPartnerDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                          {isSearchingPartners ? (
                            <div className="px-4 py-3 text-sm text-gray-500">Searching...</div>
                          ) : partnerOptions.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-gray-500">No partners found</div>
                          ) : (
                            partnerOptions.map((partner) => (
                              <button
                                key={partner.id}
                                type="button"
                                onClick={() => handleSelectPartner(partner)}
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between border-b last:border-b-0"
                              >
                                <div className="flex items-center gap-2">
                                  {partner.isOwnerPartner ? (
                                    <Crown className="h-4 w-4 text-amber-500" />
                                  ) : (
                                    <Building2 className="h-4 w-4 text-gray-400" />
                                  )}
                                  <div>
                                    <div className="font-medium">{partner.name}</div>
                                    <div className="text-xs text-gray-500">
                                      {partner.quotaUsage.isUnlimited ? 'Unlimited orgs' : `${partner.quotaUsage.organizations}/${partner.quotaUsage.maxOrganizations} orgs`}
                                    </div>
                                  </div>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  partner.type === 'internal' ? 'bg-purple-100 text-purple-700' :
                                  partner.type === 'enterprise' ? 'bg-blue-100 text-blue-700' :
                                  partner.type === 'reseller' ? 'bg-green-100 text-green-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {partner.type}
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1.5">
                    Select the partner that will manage this organization
                  </p>
                </div>

              </div>

              {/* Address Section */}
              <div className="space-y-5">
                <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                  Address
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Street *</label>
                  <Input
                    value={formData.address.street}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: {...formData.address, street: e.target.value}
                    })}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                    <Input
                      value={formData.address.city}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: {...formData.address, city: e.target.value}
                      })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
                    <Input
                      value={formData.address.state}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: {...formData.address, state: e.target.value}
                      })}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Zip Code *</label>
                    <Input
                      value={formData.address.zipCode}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: {...formData.address, zipCode: e.target.value}
                      })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
                    <Input
                      value={formData.address.country}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: {...formData.address, country: e.target.value}
                      })}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="space-y-5">
                <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                  Contact Information
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Admin Name *</label>
                  <Input
                    value={formData.contact.adminName}
                    onChange={(e) => setFormData({
                      ...formData,
                      contact: {...formData.contact, adminName: e.target.value}
                    })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Admin Email *</label>
                  <Input
                    type="email"
                    value={formData.contact.email}
                    onChange={(e) => setFormData({
                      ...formData,
                      contact: {...formData.contact, email: e.target.value}
                    })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                  <Input
                    type="tel"
                    value={formData.contact.phone}
                    onChange={(e) => setFormData({
                      ...formData,
                      contact: {...formData.contact, phone: e.target.value}
                    })}
                    required
                  />
                </div>
              </div>
            </TabsContent>

            {/* Subscription Tab - combines Subscription + Compliance. The content carries the same
                gate as the trigger: Tabs unmounts inactive content, so gating only the trigger left
                the plan catalog reachable by anything that set the active tab, and left the gate
                untestable because the content never mounted for any role on open. */}
            {canSeeSubscriptionTab && (
            <TabsContent value="subscription" className="space-y-6 mt-0">
              <SubscriptionPlanSelector
                organizationId={formData.id}
                selectedPlanId={formData.planId ?? null}
                onPlanChange={(planId) => setFormData({ ...formData, planId: planId ?? undefined })}
              />

              {/* Compliance Section. Gated separately from the subscription content above: the
                  HIPAA and BAA attestations are platform-staff decisions, not something a tenant
                  admin signs on its own behalf. */}
              {canEditCompliance && (
              <div className="space-y-5">
                <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                  Compliance & Legal
                </h3>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hipaa"
                    checked={formData.compliance?.hipaaTrainingCompleted}
                    onChange={(e) => setFormData({
                      ...formData,
                      compliance: {...formData.compliance!, hipaaTrainingCompleted: e.target.checked}
                    })}
                    className="mr-2"
                  />
                  <label htmlFor="hipaa" className="text-sm font-medium">
                    HIPAA Training Completed
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">BAA Document ID</label>
                  <Input
                    value={formData.compliance?.baaDocumentId || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      compliance: {...formData.compliance!, baaDocumentId: e.target.value}
                    })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">BAA Signed Date</label>
                    <Input
                      type="date"
                      value={toDateInputValue(formData.compliance?.baaSignedDate)}
                      onChange={(e) => setFormData({
                        ...formData,
                        compliance: {...formData.compliance!, baaSignedDate: e.target.value}
                      })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Audit Date</label>
                    <Input
                      type="date"
                      value={toDateInputValue(formData.compliance?.lastAuditDate)}
                      onChange={(e) => setFormData({
                        ...formData,
                        compliance: {...formData.compliance!, lastAuditDate: e.target.value}
                      })}
                    />
                  </div>
                </div>
              </div>
              )}
            </TabsContent>
            )}

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-6 mt-0">
              {/* Operation Mode Section */}
              <div className="space-y-5">
                <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                  Operation Mode
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Operation Mode *</label>
                  {!canManageEhrIdentity ? (
                    // A partner-scoped role's change to these three is refused by the server.
                    <div className="px-3 py-2 border rounded-md bg-gray-50 font-medium capitalize">
                      {formData.settings?.operationMode === 'connected' ? 'Connected to External EHR' : 'Standalone'}
                    </div>
                  ) : (
                  <select
                    className="w-full px-3 py-2 border rounded-md"
                    value={formData.settings?.operationMode}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: {...formData.settings!, operationMode: e.target.value as any}
                    })}
                  >
                    <option value="standalone">Standalone</option>
                    <option value="connected">Connected to External EHR</option>
                  </select>
                  )}
                  <p className="text-xs text-gray-500 mt-1.5">
                    Standalone mode uses internal patient database. Connected mode syncs with external EHR system.
                  </p>
                </div>

                {formData.settings?.operationMode === 'connected' && (
                  <>
                    <div className="p-4 bg-purple-50 border border-purple-200 rounded-md">
                      <h4 className="font-medium text-purple-900 mb-2">External EHR Integration</h4>
                      <p className="text-sm text-purple-800">
                        Connected mode requires selecting an EHR system, providing your tenant ID, and configuring API credentials.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        EHR System <span className="text-red-500 ml-1">*</span>
                      </label>
                      {!canManageEhrIdentity ? (
                        <div className="px-3 py-2 border rounded-md bg-gray-50 font-medium">
                          {formData.settings?.externalSystemId || 'Not set'}
                        </div>
                      ) : (
                      <select
                        className="w-full px-3 py-2 border rounded-md"
                        value={formData.settings?.externalSystemId || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: {...formData.settings!, externalSystemId: e.target.value}
                        })}
                        required
                      >
                        <option value="">Select EHR System...</option>
                        {(() => {
                          const current = formData.settings?.externalSystemId || '';
                          // The registry returns only ACTIVE systems; if this org is bound to a
                          // since-deactivated (or unknown) EHR, surface it as a disabled option so
                          // the current binding stays visible rather than silently blanking.
                          return current && !ehrOptions.some((o) => o.value === current) ? (
                            <option value={current} disabled>
                              {current} (inactive)
                            </option>
                          ) : null;
                        })()}
                        {ehrOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      )}
                      <p className="text-xs text-gray-500 mt-1.5">
                        The external EHR system this organization connects to for patient and encounter data.
                      </p>
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    EHR Tenant ID{formData.settings?.operationMode === 'connected' && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {!canManageEhrIdentity ? (
                    // This value plus the EHR system is the key inbound traffic is resolved by, and
                    // it has no validation of its own on the server.
                    <div className="px-3 py-2 border rounded-md bg-gray-50 font-medium">
                      {formData.settings?.externalTenantId || 'Not set'}
                    </div>
                  ) : (
                  <Input
                    value={formData.settings?.externalTenantId || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: {...formData.settings!, externalTenantId: e.target.value}
                    })}
                    placeholder="Enter your EHR tenant identifier"
                    required={formData.settings?.operationMode === 'connected'}
                  />
                  )}
                  <p className="text-xs text-gray-500 mt-1.5">
                    Your organization's unique tenant identifier in the external EHR system. Required when Operation Mode is Connected.
                  </p>
                </div>

                {formData.settings?.operationMode === 'connected' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      EHR System Timezone
                    </label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={formData.settings?.externalSystemTimezone || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: {...formData.settings!, externalSystemTimezone: e.target.value || undefined}
                      })}
                    >
                      <option value="">Not specified (assume UTC)</option>
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1.5">
                      The timezone used by the external EHR for timestamps. Imported data will be converted from this timezone to UTC.
                    </p>
                  </div>
                )}

                {/* Outbound delivery webhook (optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Webhook</label>
                  {!canManageDeliveryWebhook ? (
                    // Delivery resolves `webhookId ?? EHR default`, so a set-but-unresolvable id
                    // shadows the default and delivers nowhere.
                    <div className="px-3 py-2 border rounded-md bg-gray-50 font-medium">
                      {deliveryTargetLabel}
                    </div>
                  ) : (
                  <select
                    className="w-full px-3 py-2 border rounded-md"
                    value={formData.settings?.webhookId || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: { ...formData.settings!, webhookId: e.target.value || null }
                    })}
                  >
                    <option value="">
                      {orgEhrDefaultWebhookName ? `EHR default (${orgEhrDefaultWebhookName})` : 'None'}
                    </option>
                    {webhookOptions
                      .filter((w) => w.isActive || w.id === formData.settings?.webhookId)
                      .map((w) => (
                        <option key={w.id} value={w.id}>{w.name}{!w.isActive ? ' (inactive)' : ''}</option>
                      ))}
                  </select>
                  )}
                  {deliveryTargetInactive ? (
                    <p className="text-xs text-red-600 mt-1.5">
                      The selected webhook is inactive — deliveries are paused until it is reactivated or you clear this selection.
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1.5">
                      Where finished clinical results are delivered. Leave unset to use the EHR's default webhook (if any).
                    </p>
                  )}
                </div>
              </div>

              {/* Preferences Section */}
              <div className="space-y-5">
                <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                  Preferences
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={formData.settings?.timezone || 'America/New_York'}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: {...formData.settings!, timezone: e.target.value}
                      })}
                    >
                      {/* Any IANA zone is storable, so a stored value outside this list is appended
                          rather than left to render as a blank control. */}
                      {withStoredOption(TIMEZONE_OPTIONS, formData.settings?.timezone).map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Language</label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={formData.settings?.preferredLanguage || 'en-US'}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: {...formData.settings!, preferredLanguage: e.target.value}
                      })}
                    >
                      {/* All eleven codes the API accepts. Three were offered, so the other eight
                          rendered blank once this field became writable. */}
                      {withStoredOption(ORGANIZATION_LANGUAGE_OPTIONS, formData.settings?.preferredLanguage).map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

              </div>

              {/* Clinical Coding Features Section (edit mode, platform staff only) */}
              {mode === 'edit' && formData.id && canManageCodingFeatures && (
                <div className="space-y-5">
                  <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                    Clinical Coding Features
                  </h3>
                  {formData.settings?.operationMode !== 'connected' && (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
                      Standalone organizations do not get the E/M or CPT coding assistant; their users see diagnostic (ICD-10) coding only. These settings take effect if the organization is connected to an EHR.
                    </p>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="pr-4">
                      <label className="font-medium text-gray-900">EM Coding Assistant</label>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Show the E/M visit code section in the coding assistant and the EHR delivery confirmation. Diagnostic codes always remain visible.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        settings: {
                          ...formData.settings!,
                          emCodingAssistantEnabled: !(formData.settings?.emCodingAssistantEnabled ?? true),
                        },
                      })}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                        (formData.settings?.emCodingAssistantEnabled ?? true) ? 'bg-purple-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          (formData.settings?.emCodingAssistantEnabled ?? true) ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="pr-4">
                      <label className="font-medium text-gray-900">CPT Coding Assistant</label>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Show the procedure CPT code section in the coding assistant, the EHR delivery confirmation, and the &ldquo;Improve for coding&rdquo; button on the treatment summary. Diagnostic codes always remain visible.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        settings: {
                          ...formData.settings!,
                          cptCodingAssistantEnabled: !(formData.settings?.cptCodingAssistantEnabled ?? true),
                        },
                      })}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                        (formData.settings?.cptCodingAssistantEnabled ?? true) ? 'bg-purple-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          (formData.settings?.cptCodingAssistantEnabled ?? true) ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              )}

              {/* Organization Status Section (edit mode only) */}
              {mode === 'edit' && formData.id && (
                <div className="space-y-5">
                  <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                    Organization Status
                  </h3>

                  {/* Status Banner */}
                  {formData.isDeleted ? (
                    <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <ShieldAlert className="h-5 w-5 text-amber-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">This organization is deactivated</p>
                        <p className="text-xs text-amber-600 mt-0.5">All users have been locked out. {formData.deletedAt && `Deactivated on ${new Date(formData.deletedAt).toLocaleDateString()}.`}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-green-800">This organization is active and operational</p>
                      </div>
                    </div>
                  )}

                  {/* Deactivate / Reactivate. Gated on the same rules as the matching row actions on
                      the Organizations list: both endpoints are superadmin-only, so for every other
                      console role these buttons were a refusal the modal never showed. The status
                      banner above stays, since reading the state is not the same as changing it. */}
                  {formData.isDeleted ? (allowed('reactivateOrganization') && (
                    <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Reactivate Organization</p>
                        <p className="text-xs text-gray-500">Restore this organization and all users that were deactivated.</p>
                      </div>
                      {showReactivateConfirm ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Are you sure?</span>
                          <Button size="sm" variant="outline" onClick={() => setShowReactivateConfirm(false)} disabled={isReactivating}>Cancel</Button>
                          <Button size="sm" onClick={handleReactivate} disabled={isReactivating}>
                            {isReactivating ? 'Reactivating...' : 'Yes, reactivate'}
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" onClick={() => setShowReactivateConfirm(true)}>
                          Reactivate
                        </Button>
                      )}
                    </div>
                  )) : (allowed('deactivateOrganization') && (
                    <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Deactivate Organization</p>
                        <p className="text-xs text-gray-500">All users will be locked out and unable to access the platform.</p>
                      </div>
                      <Button size="sm" variant="destructive" onClick={initiateDeactivate}>
                        Deactivate
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Danger Zone (edit mode only). Permanent deletion cascades to every user, encounter
                  and recording, so it stays with platform staff. */}
              {mode === 'edit' && formData.id && allowed('permanentlyDeleteOrganization') && (
                <div className="space-y-4 mt-6">
                  <div className="border border-red-300 rounded-lg overflow-hidden">
                    <div className="bg-red-50 px-4 py-3 border-b border-red-200">
                      <h3 className="text-base font-semibold text-red-900 flex items-center gap-2">
                        <Trash2 className="h-4 w-4" />
                        Danger Zone
                      </h3>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Delete this organization</p>
                          <p className="text-xs text-gray-500">
                            {formData.isDeleted
                              ? 'Permanently delete this organization and all associated data. This cannot be undone.'
                              : 'You must deactivate this organization before it can be permanently deleted.'}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={!formData.isDeleted}
                          onClick={() => {
                            setHardDeleteConfirmName('');
                            setHardDeleteCheckbox(false);
                            setShowHardDeleteModal(true);
                          }}
                        >
                          Delete this organization
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
            </div>
            {/* End of Scrollable Content Area */}
          </Tabs>

          {/* Footer */}
          <div className="border-t px-6 py-4">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : mode === 'create' ? 'Create' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>

      {/* Deactivation Confirmation Modal */}
      <DeactivateConfirmModal
        isOpen={showDeactivateModal}
        entityType="organization"
        entityName={formData.name}
        cascadeImpact={cascadeImpact}
        isLoading={isDeactivating}
        onConfirm={handleDeactivateConfirm}
        onCancel={() => setShowDeactivateModal(false)}
      />

      {/* Hard Delete Confirmation Modal */}
      {showHardDeleteModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setShowHardDeleteModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg transform transition-all">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-red-200 bg-red-50 rounded-t-xl">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-red-900">Permanently Delete Organization</h2>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-5 space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    <strong>This action is permanent and irreversible.</strong> All data including users, patients,
                    appointments, clinical encounters, recordings, and medical notes will be permanently deleted.
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm text-gray-700">
                    To confirm, type{' '}
                    <span className="font-mono font-semibold bg-gray-100 px-1.5 py-0.5 rounded">{formData.name}</span>
                    {' '}below:
                  </label>
                  <input
                    type="text"
                    value={hardDeleteConfirmName}
                    onChange={(e) => setHardDeleteConfirmName(e.target.value)}
                    placeholder="Type organization name to confirm"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                    autoFocus
                    disabled={isHardDeleting}
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hardDeleteCheckbox}
                    onChange={(e) => setHardDeleteCheckbox(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    disabled={isHardDeleting}
                  />
                  <span className="text-sm text-gray-700">
                    I understand this will permanently delete all organization data and this action cannot be undone
                  </span>
                </label>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <Button variant="outline" onClick={() => setShowHardDeleteModal(false)} disabled={isHardDeleting}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleHardDelete}
                  disabled={hardDeleteConfirmName !== formData.name || !hardDeleteCheckbox || isHardDeleting}
                >
                  {isHardDeleting ? 'Deleting...' : 'I understand the consequences, delete this organization'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};

/**
 * SubscriptionPlanSelector Component
 *
 * Displays available plans from the catalog and allows assigning a plan to the organization.
 * Fetches current subscription from the Subscription model via API.
 */
interface SubscriptionPlanSelectorProps {
  organizationId?: string;
  /** Reports the selection while there is no organization to assign it to, so a create can carry it. */
  onPlanChange?: (planId: string | null) => void;
  /** The selection to render when there is no organization to hold it. */
  selectedPlanId?: string | null;
}

const SubscriptionPlanSelector: React.FC<SubscriptionPlanSelectorProps> = ({
  organizationId,
  onPlanChange,
  selectedPlanId
}) => {
  const { plans, plansLoading, plansError, fetchPlans } = useSubscriptionStore();
  const [localPlanId, setLocalPlanId] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState<{
    planId?: string;
    planName?: string;
    status?: string;
  } | null>(null);

  // Fetch plans on mount
  useEffect(() => {
    if (plans.length === 0) {
      fetchPlans();
    }
  }, [plans.length, fetchPlans]);

  // Fetch current subscription if editing existing org
  useEffect(() => {
    const fetchCurrentSubscription = async () => {
      if (!organizationId) return;

      try {
        const response = await apiService.get<{
          success: boolean;
          data: { currentSubscription?: { planId: string; plan?: { name: string }; status: string } };
        }>(`/v1/platform/subscriptions/${organizationId}/status`);

        if (response.data.currentSubscription) {
          setCurrentSubscription({
            planId: response.data.currentSubscription.planId,
            planName: response.data.currentSubscription.plan?.name,
            status: response.data.currentSubscription.status
          });
          setLocalPlanId(response.data.currentSubscription.planId || null);
        }
      } catch (error) {
        // No subscription yet - that's fine
        console.log('No current subscription found');
      }
    };

    fetchCurrentSubscription();
  }, [organizationId]);

  const activePlans = plans.filter(p => p.isActive);
  // TabsContent unmounts this pane on a tab switch, resetting local state, so in create mode the
  // parent owns the selection. With an organization the Assign button commits it and it stays here.
  const activePlanId = organizationId ? localPlanId : selectedPlanId ?? null;
  const selectedPlan = activePlans.find(p => p.id === activePlanId);

  const selectPlan = (planId: string | null) => {
    setLocalPlanId(planId);
    if (!organizationId) {
      onPlanChange?.(planId);
    }
  };

  const handleAssignPlan = async () => {
    if (!organizationId || !localPlanId) return;

    setAssigning(true);
    try {
      await apiService.post('/v1/platform/subscriptions/direct-assign', {
        organizationIds: [organizationId],
        planId: localPlanId
      });

      toast.success(`Plan "${selectedPlan?.name}" assigned successfully`);

      // Update current subscription state
      setCurrentSubscription({
        planId: localPlanId,
        planName: selectedPlan?.name,
        status: 'active'
      });
    } catch (error: any) {
      console.error('Failed to assign plan:', error);
      toast.error(error.response?.data?.error || 'Failed to assign plan');
    } finally {
      setAssigning(false);
    }
  };

  // Use shared formatPrice/formatLimit from utils (handles NaN for new pricing model)

  return (
    <div className="space-y-5">
      <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
        Subscription Plan
      </h3>

      {/* Current Subscription Info */}
      {currentSubscription && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Current Plan</p>
              <p className="font-semibold text-gray-900">
                {currentSubscription.planName || 'No plan assigned'}
              </p>
            </div>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              currentSubscription.status === 'active'
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-700'
            }`}>
              {currentSubscription.status || 'No subscription'}
            </span>
          </div>
        </div>
      )}

      {/* Plan Selection */}
      {plansLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      ) : plansError ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
          <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">Plans could not be loaded</p>
          <p className="text-sm text-gray-500 mt-1">{plansError}</p>
          <button
            type="button"
            onClick={() => fetchPlans()}
            className="mt-3 text-sm font-medium text-purple-600 hover:text-purple-700"
          >
            Try again
          </button>
        </div>
      ) : activePlans.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
          <AlertCircle className="h-10 w-10 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No Plans Available</p>
          <p className="text-sm text-gray-500 mt-1">
            Create plans in Subscription Management first.
          </p>
        </div>
      ) : (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Plan
            </label>
            <div className="grid grid-cols-1 gap-3">
              {activePlans.map((plan) => {
                const isSelected = activePlanId === plan.id;
                const isCurrent = currentSubscription?.planId === plan.id;

                return (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => selectPlan(plan.id)}
                    className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                      isSelected
                        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${tierClasses(TIER_BADGE_BORDER_CLASSES, plan.tier, 'bg-gray-100 text-gray-700 border-gray-200')}`}>
                          {plan.tier}
                        </span>
                        <span className="font-semibold text-gray-900">{plan.name}</span>
                        {isCurrent && (
                          <span className="text-xs text-gray-500">(Current)</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-gray-900">
                          {formatPrice(getEffectivePrice(plan), plan.pricing.billingCycle)}
                        </span>
                        {isSelected && (
                          <Check className="h-5 w-5 text-purple-600" />
                        )}
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500 flex gap-4">
                      <span>{formatLimit(plan.included.encountersPerMonth)} encounters/mo</span>
                      <span>{formatLimit(plan.included.transcriptionMinutes)} minutes</span>
                      <span>{formatLimit(plan.included.users)} users</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Assign Button */}
          {organizationId && localPlanId && localPlanId !== currentSubscription?.planId && (
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={handleAssignPlan}
                disabled={assigning}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {assigning ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Assign Plan
                  </>
                )}
              </button>
            </div>
          )}

        </>
      )}

      {!organizationId && (
        <div className="flex items-center justify-between gap-4 pt-2">
          <p className="text-sm text-gray-500">
            {selectedPlan
              ? `This organization will be created on ${selectedPlan.name}.`
              : 'No plan selected. The organization will be created without a subscription, and nobody inside it can use it until one is assigned.'}
          </p>
          {activePlanId && (
            <button
              type="button"
              onClick={() => selectPlan(null)}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 whitespace-nowrap"
            >
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  );
};
